import { BellOff, X } from 'lucide-react';
import { useRef, useState } from 'react';
import { api } from '../api/client';
import type { IncidentAlert, IncidentDetail, IncidentEvent, SilenceScope } from '../api/types';
import { ago, clock } from '../lib/format';
import { can, useMe } from '../lib/me';
import { useDialog } from '../lib/useDialog';
import { useLoad } from '../lib/useLoad';
import { LoadIssue } from './LoadIssue';
import { ScreenLink } from './ScreenLink';

const EVENT_TEXT: Record<string, string> = {
  opened: 'Opened',
  acknowledged: 'Acknowledged',
  escalated: 'Escalated',
  auto_resolved: 'Resolved by its check, after clean runs',
  manually_resolved: 'Resolved by hand',
  check_disabled: 'Closed: its check was retired',
};

const REASON_TEXT: Record<string, string> = {
  opened: 'Paged',
  escalated: 'Escalated to',
  resolved: 'Told it was resolved:',
};

interface Entry {
  key: string;
  at: string;
  what: string;
  who?: string | null;
  note?: string | null;
  tone?: 'bad' | 'muted';
}

const fromEvent = (event: IncidentEvent): Entry => ({
  key: `e${event.id}`,
  at: event.createdAt,
  what: EVENT_TEXT[event.kind] ?? event.kind,
  who: event.actor,
  note: event.note,
});

const fromAlert = (alert: IncidentAlert): Entry => {
  const base = `${REASON_TEXT[alert.reason] ?? alert.reason} ${alert.target} by ${alert.channel}`;
  if (alert.status === 'sent') return { key: `a${alert.id}`, at: alert.sentAt ?? alert.createdAt, what: base };
  if (alert.status === 'failed') {
    return {
      key: `a${alert.id}`,
      at: alert.createdAt,
      what: `${base} — gave up after ${alert.attempts} ${alert.attempts === 1 ? 'try' : 'tries'}`,
      note: alert.lastError,
      tone: 'bad',
    };
  }
  if (alert.status === 'pending' || alert.status === 'sending') {
    return {
      key: `a${alert.id}`,
      at: alert.createdAt,
      what: `${base} — not sent yet${alert.scheduledAt ? `, next try ${clock(alert.scheduledAt)}` : ''}`,
      note: alert.attempts ? alert.lastError : null,
      tone: 'muted',
    };
  }
  return { key: `a${alert.id}`, at: alert.createdAt, what: `${base} — ${alert.status}`, tone: 'muted' };
};

/** Durations offered for a silence; the server caps a broad one regardless. */
const DURATIONS = [
  { minutes: 30, label: '30 min' },
  { minutes: 60, label: '1 hour' },
  { minutes: 240, label: '4 hours' },
  { minutes: 1440, label: '1 day' },
  { minutes: 10080, label: '1 week' },
];

const shownFacts = (facts: Record<string, unknown> | null | undefined) =>
  Object.entries(facts || {}).filter(([key]) => !key.startsWith('_'));

interface Props {
  id: number;
  onClose: () => void;
  /** The board, so a change made here shows there without waiting a poll. */
  onChanged: () => void;
}

/**
 * One incident, whole -- opened from a row on Now, and its own address
 * (`?incident=<id>`), so the incident somebody is looking at can be pasted
 * into a chat and opens on the same thing.
 *
 * WHY IT EXISTS. The board answers "what is broken and what do I do"; it could
 * not answer the questions that come straight after. Was anybody paged, and did
 * the page arrive? Who acknowledged it, and what did they say? Where do I look
 * next? And the one that mattered most: resolving a check that is still
 * failing does nothing useful -- the next run opens a new incident and pages
 * again -- while the tool that stops pages for a known problem, a silence, had
 * an API and no button. So: the timeline (incident_events and the outbox's
 * rows, merged), the facts then and now, links to the screens that hold the
 * rest, and Acknowledge / Resolve / Silence with a note each.
 */
export function IncidentDrawer({ id, onClose, onChanged }: Props) {
  const me = useMe();
  const canRespond = can(me, 'incidents.respond');
  const panel = useRef<HTMLElement>(null);
  useDialog(panel, onClose);

  const { data, error, loading, updatedAt, reload } = useLoad(
    () => api.get<IncidentDetail>(`/incidents/${id}`),
    [id],
    15_000,
  );

  const [mode, setMode] = useState<'idle' | 'ack' | 'resolve' | 'silence'>('idle');
  const [note, setNote] = useState('');
  const [scope, setScope] = useState<SilenceScope>('incident');
  const [minutes, setMinutes] = useState(60);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const reset = (next: typeof mode) => {
    setMode(next);
    setNote('');
    setProblem(null);
  };

  const incident = data?.incident;

  const submit = async () => {
    if (!incident) return;
    setBusy(true);
    setProblem(null);
    try {
      if (mode === 'ack') {
        await api.post(`/incidents/${incident.id}/ack`, { note: note.trim() || null });
        setDone('Acknowledged.');
      } else if (mode === 'resolve') {
        await api.post(`/incidents/${incident.id}/resolve`, { note: note.trim() || null });
        setDone('Resolved.');
      } else if (mode === 'silence') {
        const body: Record<string, unknown> = { scope, minutes, reason: note.trim() };
        if (scope === 'incident') body.incidentId = incident.id;
        if (scope === 'check') body.checkId = incident.checkId;
        if (scope === 'tenant') body.tenantKey = incident.tenantKey;
        const answer = await api.post<{ silence: { expiresAt: string }; note?: string }>('/silences', body);
        setDone(
          `Silenced until ${new Date(answer.silence.expiresAt).toLocaleString()}.${answer.note ? ` ${answer.note}.` : ''}`,
        );
      }
      reset('idle');
      await reload();
      onChanged();
    } catch (caught) {
      // A 409 is usually a teammate getting there first: say what the server
      // said, and re-read so the drawer shows what they did.
      setProblem((caught as Error).message);
      void reload();
    } finally {
      setBusy(false);
    }
  };

  const entries = data
    ? [...data.events.map(fromEvent), ...data.alerts.map(fromAlert)].sort(
        (a, b) => new Date(a.at).getTime() - new Date(b.at).getTime(),
      )
    : [];
  const now = shownFacts(incident?.facts);
  const then = shownFacts(incident?.firstFacts);
  const changed = then.length > 0 && JSON.stringify(then) !== JSON.stringify(now);
  const open = incident && incident.status !== 'resolved';
  const docsIsUrl = incident?.docsUrl ? /^https?:\/\//.test(incident.docsUrl) : false;

  return (
    <>
      <div className="wt-drawer-backdrop" onClick={onClose} />
      <aside
        ref={panel}
        className="wt-drawer wt-inc-drawer"
        role="dialog"
        aria-modal="true"
        aria-label={incident ? `Incident: ${incident.title}` : 'Incident'}
      >
        <header className="wt-drawer-head">
          <div className="wt-drawer-titles">
            <div className="wt-drawer-title">{incident?.title ?? (loading ? 'Loading…' : `Incident ${id}`)}</div>
            {incident && (
              <div className="wt-drawer-sub">
                <span className={`wt-sev-label wt-sev-label-${incident.severity}`}>{incident.severity}</span>{' '}
                {incident.status} · {incident.system}
                {incident.tenantKey && ` · ${incident.tenantKey}`}
                {incident.ref && ` · ${incident.ref}`} · #{incident.id}
              </div>
            )}
          </div>
          <button type="button" className="btn btn-ghost btn-icon" title="Close (Esc)" aria-label="Close" onClick={onClose}>
            <X size={16} />
          </button>
        </header>

        <LoadIssue error={error} updatedAt={updatedAt} what="this incident" onRetry={() => void reload()} />

        {incident && (
          <>
            <p className="wt-inc-summary">{incident.summary}</p>
            {incident.remedy && <div className="wt-remedy">→ {incident.remedy}</div>}

            <div className="wt-inc-flags">
              {incident.shadow && <span className="wt-chip wt-chip-shadow">shadow · nobody is paged</span>}
              {incident.flapping && <span className="wt-chip wt-chip-flap">flapping · its open did not page</span>}
              {!incident.shadow && (
                <span className={`wt-chip ${incident.notifiedAt ? '' : 'wt-chip-flap'}`}>
                  {incident.notifiedAt ? `paged ${ago(incident.notifiedAt)}` : 'nobody paged yet'}
                </span>
              )}
              {incident.escalatedAt && <span className="wt-chip">escalated {ago(incident.escalatedAt)}</span>}
              {incident.suppressedBySilenceId && (
                <span className="wt-chip wt-chip-shadow">held by silence #{incident.suppressedBySilenceId}</span>
              )}
              {incident.status === 'acknowledged' && (
                <span className="wt-chip wt-chip-ack">
                  ack {incident.acknowledgedBy}
                  {incident.acknowledgedAt && ` · ${ago(incident.acknowledgedAt)}`}
                </span>
              )}
              {incident.previousIncidentId && <span className="wt-chip">reopened (was #{incident.previousIncidentId})</span>}
            </div>

            <section className="wt-inc-section">
              <h3>Where to look</h3>
              <div className="wt-inc-links">
                {can(me, 'checks.view') && (
                  <ScreenLink to="/checks" previewTab="checks" className="btn btn-secondary">
                    Its check{incident.checkTitle ? `: ${incident.checkTitle}` : ''}
                  </ScreenLink>
                )}
                {incident.tenantKey && can(me, 'tenants.view') && (
                  <ScreenLink
                    to="/tenants"
                    previewTab="tenants"
                    search={`tenant=${encodeURIComponent(incident.tenantKey)}`}
                    className="btn btn-secondary"
                  >
                    {incident.tenantKey} on Tenants
                  </ScreenLink>
                )}
                {incident.tenantKey && incident.system === 'dispatch-be' && can(me, 'audit.view') && (
                  <ScreenLink
                    to="/audit"
                    previewTab="audit"
                    search={`tenant=${encodeURIComponent(incident.tenantKey)}`}
                    className="btn btn-secondary"
                  >
                    {incident.tenantKey}'s audit trail
                  </ScreenLink>
                )}
                {/queue/.test(incident.checkId) && can(me, 'queues.view') && (
                  <ScreenLink to="/queues" previewTab="queues" className="btn btn-secondary">
                    Queues
                  </ScreenLink>
                )}
                {can(me, 'systems.view') && (
                  <ScreenLink
                    to="/systems/logs"
                    previewTab="logs"
                    search={`q=${encodeURIComponent(incident.checkId)}`}
                    className="btn btn-secondary"
                  >
                    sphere-watch's log for it
                  </ScreenLink>
                )}
                {incident.docsUrl &&
                  (docsIsUrl ? (
                    <a className="btn btn-secondary" href={incident.docsUrl} target="_blank" rel="noreferrer">
                      Runbook
                    </a>
                  ) : (
                    <span className="wt-inc-doc">
                      Runbook: <code>{incident.docsUrl}</code>
                    </span>
                  ))}
              </div>
            </section>

            {canRespond && open && (
              <section className="wt-inc-section">
                <h3>Respond</h3>
                {done && mode === 'idle' && <p className="note note-ok">{done}</p>}
                {mode === 'idle' && (
                  <div className="wt-inc-links">
                    {incident.status === 'open' && (
                      <button type="button" className="btn btn-primary" onClick={() => reset('ack')}>
                        Acknowledge…
                      </button>
                    )}
                    <button type="button" className="btn btn-secondary" onClick={() => reset('silence')}>
                      <BellOff size={13} /> Silence…
                    </button>
                    <button type="button" className="btn btn-ghost" onClick={() => reset('resolve')}>
                      Resolve…
                    </button>
                  </div>
                )}
                {mode !== 'idle' && (
                  <form
                    className="wt-inc-form"
                    onSubmit={(event) => {
                      event.preventDefault();
                      void submit();
                    }}
                  >
                    {mode === 'resolve' && (
                      <p className="note">
                        Resolving closes it now. If its check still finds the problem, the next run opens a new
                        incident and pages again — to stop pages for something known, silence it instead.
                      </p>
                    )}
                    {mode === 'silence' && (
                      <>
                        <p className="note">
                          A silence holds pages back until it ends; the incident stays on the board, and anything
                          still owed is sent when it lifts.
                        </p>
                        <fieldset className="wt-inc-scope">
                          <legend>Silence</legend>
                          <label>
                            <input
                              type="radio"
                              name="scope"
                              checked={scope === 'incident'}
                              onChange={() => setScope('incident')}
                            />{' '}
                            this incident
                          </label>
                          <label>
                            <input
                              type="radio"
                              name="scope"
                              checked={scope === 'check'}
                              onChange={() => setScope('check')}
                            />{' '}
                            every incident of its check{incident.tenantKey ? ', every tenant' : ''}
                          </label>
                          {incident.tenantKey && (
                            <label>
                              <input
                                type="radio"
                                name="scope"
                                checked={scope === 'tenant'}
                                onChange={() => setScope('tenant')}
                              />{' '}
                              everything about {incident.tenantKey}
                            </label>
                          )}
                        </fieldset>
                        <label className="wt-inc-field">
                          For{' '}
                          <select className="select" value={minutes} onChange={(event) => setMinutes(Number(event.target.value))}>
                            {DURATIONS.map((option) => (
                              <option key={option.minutes} value={option.minutes}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </label>
                      </>
                    )}
                    <label className="wt-inc-field">
                      {mode === 'silence' ? 'Why (required)' : 'Note (optional)'}
                      <textarea
                        className="textarea"
                        value={note}
                        rows={2}
                        required={mode === 'silence'}
                        minLength={mode === 'silence' ? 4 : undefined}
                        placeholder={
                          mode === 'silence'
                            ? 'e.g. dispatch maintenance until 06:00, ticket KAN-123'
                            : mode === 'ack'
                              ? 'e.g. looking — the CRM worker is restarting'
                              : 'e.g. fixed by redeploying the worker'
                        }
                        onChange={(event) => setNote(event.target.value)}
                      />
                    </label>
                    {problem && (
                      <p className="note note-danger" role="alert">
                        {problem}
                      </p>
                    )}
                    <div className="wt-inc-links">
                      <button
                        type="submit"
                        className={`btn ${mode === 'resolve' ? 'btn-danger' : 'btn-primary'}`}
                        disabled={busy || (mode === 'silence' && note.trim().length < 4)}
                      >
                        {mode === 'ack' ? 'Acknowledge' : mode === 'resolve' ? 'Resolve now' : 'Silence'}
                      </button>
                      <button type="button" className="btn btn-ghost" disabled={busy} onClick={() => reset('idle')}>
                        Cancel
                      </button>
                    </div>
                  </form>
                )}
              </section>
            )}

            <section className="wt-inc-section">
              <h3>What it found</h3>
              {now.length ? (
                <dl className="wt-inc-facts">
                  {now.map(([key, value]) => (
                    <div key={key}>
                      <dt>{key}</dt>
                      <dd>{String(value)}</dd>
                    </div>
                  ))}
                </dl>
              ) : (
                <p className="note">No facts recorded.</p>
              )}
              {changed && (
                <details className="wt-inc-then">
                  <summary>When it opened ({ago(incident.firstSeenAt)})</summary>
                  <dl className="wt-inc-facts">
                    {then.map(([key, value]) => (
                      <div key={key}>
                        <dt>{key}</dt>
                        <dd>{String(value)}</dd>
                      </div>
                    ))}
                  </dl>
                </details>
              )}
              <p className="wt-drawer-foot">
                First seen {ago(incident.firstSeenAt)}, last seen {ago(incident.lastSeenAt)}, {incident.occurrences}{' '}
                {incident.occurrences === 1 ? 'run' : 'runs'}.
              </p>
            </section>

            <section className="wt-inc-section">
              <h3>What happened</h3>
              {entries.length ? (
                <ol className="wt-inc-timeline">
                  {entries.map((entry) => (
                    <li key={entry.key} className={entry.tone ? `tone-${entry.tone}` : undefined}>
                      <time dateTime={entry.at} title={new Date(entry.at).toLocaleString()}>
                        {ago(entry.at)}
                      </time>
                      <span>
                        {entry.what}
                        {entry.who && <span className="wt-inc-who"> · {entry.who}</span>}
                        {entry.note && <span className="wt-inc-note">“{entry.note}”</span>}
                      </span>
                    </li>
                  ))}
                </ol>
              ) : (
                <p className="note">Nothing recorded yet.</p>
              )}
            </section>
          </>
        )}
      </aside>
    </>
  );
}
